import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ELASTIC_SEARCH_CONSTANTS } from '@constants/elastic-search/elastic-search-constants.constant';
import { ELASTIC_SEARCH_FIELDS } from '@constants/elastic-search/elastic-search-fields.constant';
import { ELASTIC_SEARCH_RETURNED_FIELDS } from '@constants/elastic-search/elastic-search-returned-fields.constant';
import { ELASTIC_SEARCH_TYPE } from '@constants/elastic-search/elastic-search-type.enum';
import { HTTPS_REQUEST_OPTIONS } from '@constants/https-request-options/https-request-options';
import { USER_ROLE } from '@constants/user-role.constant';
import { DashboardConfiguration } from '@models/dashboard/dashboard-configuration';
import { DashboardGroup } from '@models/dashboard/dashboard-group';
import { DashboardMachine } from '@models/dashboard/dashboard-machine';
import { DashboardListCategoryMachines } from '@models/dashboard/machine-list/dashboard-list-category-machines';
import { MachineIdList } from '@models/dashboard/machine-list/machine-id-list';
import { ElasticSearchFilterValue } from '@models/elastic-search/request/elastic-search-filter-value';
import { ElasticSearchRequest } from '@models/elastic-search/request/elastic-search-request';
import { ELASTIC_SEARCH_SORT_ORDER } from '@models/elastic-search/request/elastic-search-sort-order.enum';
import { ElasticSearchSortValue } from '@models/elastic-search/request/elastic-search-sort-value';
import { ElasticSearchResponseEntity } from '@models/elastic-search/response/elastic-search-response-entity';
import { Machine } from '@models/machine/machine';
import { MenuItem } from '@models/sidebar/menu-item';
import { MenuItemType } from '@models/sidebar/menu-item-type';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { MachineCategoryService } from '@services/administration/portal-setup/machine-category/machine-category.service';
import { ApiLocationService } from '@services/api-location/api-location.service';
import { MachineDetailsService } from '@services/dashboard/machine-details.service';
import { DesignService } from '@services/left-menu/design/design.service';
import { LeftMenuService } from '@services/left-menu/left-menu.service';
import { StorageService } from '@services/storage/storage.service';
import { lastValueFrom, Observable, Subscriber, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  private readonly CONFIGURATION_STORAGE_KEY_PREFIX = 'dashboardConfiguration_';
  private ELASTIC_API_PREFIX = '/api/elastic/';
  private ITEMS_COUNT_SUFFIX = '/count';

  private readonly BASE_URL: string;
  private GET_DASHBOARD_LIST_REFRESH_INTERVAL_URL_SUFFIX: string = '/api/web/tenant/0/machine/0/listview/dashboardRefreshInterval';

  constructor(private http: HttpClient,
              private apiLocationService: ApiLocationService,
              private leftMenuService: LeftMenuService,
              private designService: DesignService,
              private machineDetailsService: MachineDetailsService,
              private machineCategoryService: MachineCategoryService,
              private storageService: StorageService) {
    this.BASE_URL = this.apiLocationService.getLocation();
  }

  public getTileConfiguration(machineId: number): Observable<DashboardConfiguration> {
    const configurationsKey: string = this.CONFIGURATION_STORAGE_KEY_PREFIX + machineId;
    return new Observable<DashboardConfiguration>((observer: Subscriber<DashboardConfiguration>) => {
      const configurationsPromise: Promise<any> = this.storageService.readValueFromIndexedDb<DashboardConfiguration>(configurationsKey);
      configurationsPromise.then((result) => {
        if (result !== undefined) {
          observer.next(JSON.parse(result));
        }
        this.getDashboardConfigurationsFromBackend(machineId, observer);

      }, (error) => {
        console.error('Error getting dashboard configurations from indexedDb. Loading from backend', error);
        this.getDashboardConfigurationsFromBackend(machineId, observer);
      });
    });
  }

  public getItemMenuItemAndParentMenuItem(id: string, itemIsMachine: boolean): Observable<MenuItem> {
    let resultMenuItem: MenuItem = new MenuItem();

    return new Observable<MenuItem>((observer: Subscriber<MenuItem>) => {
      this.leftMenuService.getParent(id, itemIsMachine).subscribe({
        next: menuItem => {
          resultMenuItem = Object.assign(new MenuItem(), menuItem);
          resultMenuItem.icon = this.designService.getIcon(resultMenuItem.type);
          this.getMenuItemParent(observer, resultMenuItem);
        },
        error: (_error) => {
          observer.next(resultMenuItem);
        }
      });
    });
  }

  public getGroupStatusPageMenuItem(groupId: string): Observable<MenuItem> {
    let resultMenuItem: MenuItem = new MenuItem();
    let subscription: Subscription | undefined;

    return new Observable<MenuItem>((observer: Subscriber<MenuItem>) => {
      this.leftMenuService.getMenuItems(String(groupId), true).subscribe({
        next: menuItems => {
          const groupStatusPageMenuitem = menuItems.find(item => item.type === MenuItemType.MACHINE_GROUP_STATUS_PAGE);
          if (groupStatusPageMenuitem) {
            resultMenuItem = Object.assign(new MenuItem(), groupStatusPageMenuitem);
            resultMenuItem.icon = this.designService.getIcon(groupStatusPageMenuitem.type);
            this.getMenuItemParent(observer, resultMenuItem);
          } else {
            this.leftMenuService.getParent(groupId, false).subscribe({
              next: menuItem => {
                resultMenuItem = Object.assign(new MenuItem(), menuItem);
                resultMenuItem.icon = this.designService.getIcon(resultMenuItem.type);
                this.getMenuItemParent(observer, resultMenuItem);
              },
              error: (_error) => {
                observer.next(resultMenuItem);
              }
            });
          }
        },
        error: (_error) => {
          observer.next(resultMenuItem);
        }
      });
    }).pipe(finalize(() => {
      if (subscription) {
        subscription.unsubscribe();
      }
    }));
  }

  public getDashboardTileMachinesFromElastic(machineryGroupId: string, organizationId: number, location: string, pageIndex: number, limit: number, whiteListedMachineryGroups: number[] | null): Observable<Set<DashboardMachine>> {
    return new Observable<Set<DashboardMachine>>((observer: Subscriber<Set<DashboardMachine>>) => {
      this.listDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINE, machineryGroupId, organizationId, location, null, null, pageIndex, limit, whiteListedMachineryGroups).subscribe({
        next: (success: ElasticSearchResponseEntity) => {
          const machines = this.convertMachinesToDashboardMachines(success.result.data);
          observer.next(machines);
        },
        error: (_error) => {
          observer.next(new Set<DashboardMachine>());
        }
      });
    });
  }

  public getDashboardListMachinesFromElastic(
      machineryGroupId: string,
      organizationId: number,
      location: string,
      category: string | null,
      sortValue: ElasticSearchSortValue,
      pageIndex: number,
      limit: number,
      whiteListedMachineryGroups: number[] | null,
      searchText: string = '*'
  ): Observable<DashboardListCategoryMachines> {
    return new Observable<DashboardListCategoryMachines>((observer: Subscriber<DashboardListCategoryMachines>) => {
      this.listDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINE, machineryGroupId, organizationId, location, category, sortValue, pageIndex, limit, whiteListedMachineryGroups, searchText).subscribe({
        next: (success: ElasticSearchResponseEntity) => {
          const result: DashboardListCategoryMachines = new DashboardListCategoryMachines();
          result.machines = this.convertMachinesToDashboardMachines(success.result.data);
          result.machinesCount = success.result.totalHits;
          observer.next(result);
        }, error: (_error) => {
          observer.next(new DashboardListCategoryMachines());
        }
      });
    });
  }

  public getMachineDetailsFromElastic(machineId: number, organizationId: number | null, whiteListedMachineryGroups: number[] | null): Observable<DashboardMachine | null> {
    return new Observable<DashboardMachine | null>((observer: Subscriber<DashboardMachine | null>) => {
      const request = new ElasticSearchRequest();
      request.fieldsToSearch = ELASTIC_SEARCH_FIELDS.MACHINE_SEARCH_FIELDS;
      request.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINE_RETURNED_FIELDS;
      request.from = 0;
      request.size = 1;
      request.searchText = '*';
      request.sort = new ElasticSearchSortValue('name', ELASTIC_SEARCH_SORT_ORDER.ASC);
      request.filter = [new ElasticSearchFilterValue('id', String(machineId))];
      if (organizationId && organizationId > 0) {
        request.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.ORGANIZATION_ANCESTORS_PROPERTY_NAME, String(organizationId)));
      }
      if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
        request.whiteListFilter = [new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, String(whiteListedMachineryGroups.join(',')))];
      }
      const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + ELASTIC_SEARCH_TYPE.MACHINE + '/';
      this.http.post<ElasticSearchResponseEntity>(url, request, HTTPS_REQUEST_OPTIONS).subscribe({
        next: (success: ElasticSearchResponseEntity) => {
          const machines = this.convertMachinesToDashboardMachines(success.result?.data ?? []);
          const first = Array.from(machines)[0] ?? null;
          observer.next(first);
        }, error: (_error) => {
          observer.next(null);
        },
      });
    });
  }


  public getDashboardGroupsFromElastic(machineryGroupId: string, organizationId: number | null, user: User): Observable<DashboardGroup[]> {
    const pageIndex = 1;
    const limit = 100;

    const isSuperAdmin: boolean = user.role == USER_ROLE.SUPERADMIN;
    const availableWhiteListedMachineryGroups: number[] =
        user.getWhiteListedMachineryGroups(true).length == 0 || isSuperAdmin ? [] : user.getWhiteListedMachineryGroups();

    return new Observable<DashboardGroup[]>((observer: Subscriber<DashboardGroup[]>) => {

      this.listDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINERY_GROUP, machineryGroupId, organizationId, null, null, null, pageIndex, limit, availableWhiteListedMachineryGroups).subscribe({
        next: (success: ElasticSearchResponseEntity) => {
          const result: DashboardGroup[] = [];
          const machineryGroups = success.result.data;
          const countPromises: Promise<ElasticSearchResponseEntity>[] = [];
          for (const value of machineryGroups) {
            const group: DashboardGroup = {
              groupId: value.id,
              name: value.name,
              machinesCount: -1,
              groupsCount: -1,
              disabled: this.shouldDisableGroup(value.id, user)
            };

            if (group.disabled) {
              countPromises.push(lastValueFrom(this.countDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINERY_GROUP, value.id, organizationId, null, availableWhiteListedMachineryGroups)).then(r => group.groupsCount = r.result.totalHits));
            } else {
              const orgId = user.role == USER_ROLE.SUPERADMIN ? organizationId : user.parentId;
              countPromises.push(lastValueFrom(this.countDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINE, value.id, orgId, null, availableWhiteListedMachineryGroups)).then(r => group.machinesCount = r.result.totalHits));
              countPromises.push(lastValueFrom(this.countDashboardItemsOfGroup(ELASTIC_SEARCH_TYPE.MACHINERY_GROUP, value.id, organizationId, null, availableWhiteListedMachineryGroups)).then(r => group.groupsCount = r.result.totalHits));
            }
            result.push(group);
          }
          Promise.all(countPromises).then(() => observer.next(result));
          observer.next(result);
        }, error: (_error) => {
          observer.next([]);
        }
      });
    });
  }

  private shouldDisableGroup(groupId: number, user: User): boolean {
    const role = new Role(user);
    const activeWhiteListedMachineryGroups = user.getWhiteListedMachineryGroups(true);
    if (role.isSuperAdminRole || activeWhiteListedMachineryGroups.length === 0) {
      return false;
    }
    return !activeWhiteListedMachineryGroups?.includes(groupId);
  }


  public listDashboardItemsOfGroup(type: ELASTIC_SEARCH_TYPE, machineryGroupId: string, organizationId: number | null, location: string | null, category: string | null, sortValue: ElasticSearchSortValue | null, pageIndex: number, limit: number, whiteListedMachineryGroups: number[] | null, searchText: string = '*'): Observable<any> {
    const listRequest: ElasticSearchRequest = new ElasticSearchRequest();
    const whiteListFilters: ElasticSearchFilterValue[] = [];
    const parentGroupFilterValue: ElasticSearchFilterValue = new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, machineryGroupId);
    if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
      if (whiteListedMachineryGroups.includes(Number(machineryGroupId))) {
        listRequest.filter = [parentGroupFilterValue];
      } else {
        listRequest.filter = [new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, '-1')];
      }
    } else {
      listRequest.filter = [parentGroupFilterValue];
    }

    if (organizationId && organizationId > 0) {
      listRequest.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.ORGANIZATION_ANCESTORS_PROPERTY_NAME, String(organizationId)));
    }
    if (type === ELASTIC_SEARCH_TYPE.MACHINERY_GROUP) {
      listRequest.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINERY_GROUP_RETURNED_FIELDS;
      listRequest.fieldsToSearch = ELASTIC_SEARCH_FIELDS.MACHINERY_GROUP_SEARCH_FIELDS;
      if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
        whiteListFilters.push(new ElasticSearchFilterValue('id', String(whiteListedMachineryGroups.join(','))));
      }
    } else {
      listRequest.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINE_RETURNED_FIELDS;
      listRequest.fieldsToSearch = ELASTIC_SEARCH_FIELDS.MACHINE_SEARCH_FIELDS;
      if (location && !location.isNullOrWhitespace()) {
        listRequest.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINE_LOCATION_FIELD, location, true));
      }
      if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
        whiteListFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, String(whiteListedMachineryGroups.join(','))));
      }

    }

    if (category && !category.isNullOrWhitespace()) {
      listRequest.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINE_CATEGORY_FIELD, category, true));
    }

    if (sortValue && sortValue.order !== ELASTIC_SEARCH_SORT_ORDER.NONE) {
      listRequest.sort = sortValue;
    }
    listRequest.whiteListFilter = whiteListFilters;
    listRequest.searchText = searchText && searchText.length > 0 ? searchText : '*';
    listRequest.from = (pageIndex - 1) * limit;
    listRequest.size = limit;
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    return this.http.post(url, listRequest, HTTPS_REQUEST_OPTIONS);
  }


  public countDashboardItemsOfGroup(type: ELASTIC_SEARCH_TYPE, machineryGroupId: string, organizationId: number | null, location: string | null, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const countRequest: ElasticSearchRequest = new ElasticSearchRequest();

    const parentGroupFilterValue: ElasticSearchFilterValue = new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, machineryGroupId);
    countRequest.filter = [parentGroupFilterValue];
    if (organizationId && organizationId > 0) {
      countRequest.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.ORGANIZATION_ANCESTORS_PROPERTY_NAME, String(organizationId)));
    }

    if (type === ELASTIC_SEARCH_TYPE.MACHINE) {
      if (location && !location.isNullOrWhitespace()) {
        countRequest.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINE_LOCATION_FIELD, location, true));
      }
      if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
        countRequest.whiteListFilter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, String(whiteListedMachineryGroups.join(','))));
      }

    } else {
      if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
        countRequest.whiteListFilter.push(new ElasticSearchFilterValue('id', String(whiteListedMachineryGroups.join(','))));
      }
    }


    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + this.ITEMS_COUNT_SUFFIX;
    return this.http.post(url, countRequest, HTTPS_REQUEST_OPTIONS);
  }


  public getDashboardItemsAggregateDetailsForGroup(machineryGroupId: string, organizationId: number | null, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const countRequest: ElasticSearchRequest = new ElasticSearchRequest();
    const whiteListFilters: ElasticSearchFilterValue[] = [];
    const parentGroupFilterValue: ElasticSearchFilterValue = new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, machineryGroupId);
    countRequest.filter = [parentGroupFilterValue];
    if (organizationId && organizationId > 0) {
      countRequest.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.ORGANIZATION_ANCESTORS_PROPERTY_NAME, String(organizationId)));
    }

    if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
      whiteListFilters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, String(whiteListedMachineryGroups.join(','))));
    }

    const requiredFields = [ELASTIC_SEARCH_CONSTANTS.MACHINE_LOCATION_FIELD, ELASTIC_SEARCH_CONSTANTS.MACHINE_CATEGORY_FIELD];
    countRequest.aggregateFields = requiredFields;
    countRequest.fieldsToSearch = requiredFields;
    countRequest.fieldsToReturn = requiredFields;
    countRequest.searchText = '*';
    countRequest.from = 0;
    countRequest.size = 1;
    countRequest.whiteListFilter = whiteListFilters;

    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + ELASTIC_SEARCH_TYPE.MACHINE + '/';
    return this.http.post(url, countRequest, HTTPS_REQUEST_OPTIONS);
  }

  public sortMachinesList(machines: (DashboardMachine)[]): (DashboardMachine)[] {
    if (machines === undefined || machines.length === 0) {
      return [];
    }

    return machines.sort(function (m1, m2) {
      return (((m1.location && m2.location) && (m1.location.toLowerCase() > m2.location.toLowerCase())) ? 1 : -1);
    });
  }

  public getMachineCategoriesConfiguration() {
    return this.machineCategoryService.listColumnConfigurations();
  }

  public getDashboardListRefreshInterval(): Observable<any> {
    const url: string = this.BASE_URL + this.GET_DASHBOARD_LIST_REFRESH_INTERVAL_URL_SUFFIX;
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  private getDashboardConfigurationsFromBackend(machineId: number, observer: Subscriber<any>) {
    const machineIdList = new MachineIdList();

    machineIdList.machineIds = [machineId];
    this.machineDetailsService.listAllDashboardConfigurations(machineIdList).subscribe({
      next: (result) => {
        if (!result || !result[machineId]) {
          observer.next(new DashboardConfiguration());
        } else {
          const config = result[machineId];
          this.saveDashboardConfigurationsInStorage(machineId, config);
          observer.next(config);
        }
      }, error: (error) => {
        console.error('Error getting dashboard configurations', error);
        observer.next(new DashboardConfiguration());
      }
    });
  }

  private saveDashboardConfigurationsInStorage(machineId: number, config: DashboardConfiguration): void {
    const configurationsKey: string = this.CONFIGURATION_STORAGE_KEY_PREFIX + machineId;
    this.storageService.writeValueToIndexedDb(configurationsKey, config);
  }

  private convertMachinesToDashboardMachines(machines: Machine[]): Set<DashboardMachine> {
    const dashboardMachines: Set<DashboardMachine> = new Set<DashboardMachine>();
    machines.forEach(value => {
      dashboardMachines.add(this.convertMachineToDashboardMachine(value));
    });

    return dashboardMachines;
  }

  private convertMachineToDashboardMachine(machine: Machine): DashboardMachine {
    const result: DashboardMachine = new DashboardMachine();
    result.id = machine.id || -1;
    result.parentId = machine.parentId;
    result.location = machine.location;
    result.locationDetails = machine.locationDetails;
    result.name = machine.name;
    result.type = machine.type;
    result.category = machine.category;
    result.tenant = String(machine.tenant);
    result.trafficLightConfiguration = machine.trafficLightConfiguration;
    result.projectId = machine.projectId;
    result.datasetLocation = machine.datasetLocation;
    result.tableauProjectId = machine.tableauProjectId;
    result.machineTag = machine.machineTag;
    result.chartConfig = {};
    if (machine.chartConfig != null && machine.chartConfig.statusIndicator != null) {
      result.chartConfig.statusIndicator = machine.chartConfig.statusIndicator;
    }

    return result;
  }

  private getMenuItemParent(observer: any, resultMenuItem: MenuItem): void {
    if (!resultMenuItem.parentId) {
      observer.next(resultMenuItem);
      return;
    }

    this.leftMenuService.getParent(resultMenuItem.parentId, false).subscribe({
      next: menuItemParent => {
        const parent = Object.assign(new MenuItem(), menuItemParent);
        parent.icon = this.designService.getIcon(parent.type);
        resultMenuItem.menuItemParent = parent;
        observer.next(resultMenuItem);
      }, error: (_error) => {
        observer.next(resultMenuItem);
      }
    });

  }


}
